import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { TariffUpdateController } from '@app/feature/tariff/tariff-update/tariff-update.controller';
import {
  TariffContextProvider,
  TariffUIProvider,
} from '@app/feature/tariff/tariff.context';
import { useCpmsBoGetTariffQuery } from '@service/cpms-bo-tariff.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import * as css from './tariff-details.module.scss';
import { TariffDetailsView } from './tariff-details.view';

interface TariffDetailsControllerProps {
  resource: TariffModel;
}

const TariffDetailsController = (
  props: TariffDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.name ?? resource.id);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.TARIFFS,
        action: ActionsByResource.TARIFFS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          onClick={() => setActionUpdateIsDialogOpen(true)}
          icon="edit-05"
          key="update"
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <div className={css.tariffDetailContainer}>
      <TariffContextProvider data={resource}>
        <TariffDetailsView />
        <Dialog
          onClose={() => setActionUpdateIsDialogOpen(false)}
          open={actionUpdateIsDialogOpen}
          height="100%"
          size={DialogSize.LARGE}>
          <Dialog.Header
            onClose={() => setActionUpdateIsDialogOpen(false)}
            title={resource.name ?? resource.id}
          />
          <TariffUIProvider>
            <TariffUpdateController
              tariff={resource}
              onSuccessfulUpdate={() => setActionUpdateIsDialogOpen(false)}
              onCancel={() => setActionUpdateIsDialogOpen(false)}
            />
          </TariffUIProvider>
        </Dialog>
      </TariffContextProvider>
    </div>
  );
};

export const TariffDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetTariffQuery(params.id ?? '');

  const { Component } = withProtectedResource<TariffModel, {}, TariffModel>(
    TariffDetailsController,
    queryState,
    {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.TARIFFS,
          action: ActionsByResource.TARIFFS.READ,
        }),
    },
  )({});

  return Component;
};
